import { LanguageID } from "configs";
import { Dayjs } from "dayjs";
import { dayjsFormatTZ, dayjsTZ } from "helpers";
import camelCase from "lodash/camelCase";
import _get from "lodash/get";
import upperFirst from "lodash/upperFirst";
import { PayrollCycleType } from "pages/PayrollSetting/PayrollCycle/types";
import { PayrollCycle } from "services";
import { i18n } from "translations";

import { loadLocalStorage } from "./storage";

export interface AnyObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyArray = any[];

export const capitalize = (str: string): string => upperFirst(camelCase(str));

export function filterObject(obj: AnyObject): AnyObject {
  const transformedObj: AnyObject = {};
  for (const [key, value] of Object.entries(obj)) {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      (typeof value === "object" && Object.keys(value).length === 0)
    ) {
      transformedObj[key] = null;
    } else {
      transformedObj[key] = value;
    }
  }
  return transformedObj;
}

export function checkRequireValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

export const getOptionsSelect = <T>({
  data,
  keyLabel,
  keySubLabel,
  keyValue,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  keyLabel: string;
  keySubLabel?: string;
  keyValue: string;
}) => {
  return (
    (Array.isArray(data) &&
      data?.map((item) => ({
        label:
          (item[keyLabel] as string) +
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          (keySubLabel ? ` (${item[keySubLabel]})` : ""),
        value: item[keyValue] as T,
        original: item,
      }))) ||
    ([] as { label: string; value: T }[])
  );
};

export const getErrorMessage = (
  errors: AnyObject,
  fieldName: string
): AnyObject | null => {
  return _get(errors, fieldName, null) as AnyObject;
};

export const getRequiredMessage = (fieldName = ""): string => {
  return i18n.t("requiredField")?.replace("{FIELD}", fieldName) || "";
};

export const getRequiredI18n = (fieldName = ""): string => {
  return i18n.t("requiredField")?.replace("{FIELD}", i18n.t(fieldName)) || "";
};

export const getPlaceholderSelect = (fieldName = ""): string => {
  return (
    i18n.t("selectOptionField")?.replace("{FIELD}", fieldName?.toLowerCase()) ||
    ""
  );
};

export const getPlaceholderText = (fieldName = ""): string => {
  return (
    i18n.t("typeField")?.replace("{FIELD}", fieldName?.toLowerCase()) || ""
  );
};

export const getInvalidMessage = (fieldName = ""): string => {
  return (
    i18n.t("invalidField")?.replace("{FIELD}", fieldName?.toLowerCase()) || ""
  );
};

export const getInvalidMessageI18n = (fieldName = ""): string => {
  return i18n.t("invalidField")?.replace("{FIELD}", i18n.t(fieldName)) || "";
};

export const getDefaultValueFromURL = (
  searchLocation: Record<string, unknown>,
  fieldName: string,
  type: "autocomplete" | "multi-autocomplete" | "date" | "date-range"
) => {
  if (type === "autocomplete") {
    if (!_get(searchLocation, `${fieldName}Value`, "")) return null;
    return {
      label: _get(searchLocation, `${fieldName}Label`, ""),
      value: _get(searchLocation, `${fieldName}Value`, ""),
    };
  }
  if (type === "multi-autocomplete") {
    if (typeof searchLocation?.[`${fieldName}Labels`] === "string") {
      return [
        {
          label: _get(searchLocation, `${fieldName}Labels`, ""),
          value: _get(searchLocation, `${fieldName}Values`, ""),
        },
      ];
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Array.isArray(searchLocation?.[`${fieldName}Values`])
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        (searchLocation?.[`${fieldName}Values`] as any)?.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (value: any, index: any) => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: (searchLocation?.[`${fieldName}Labels`] as any)?.[index],
            value,
          })
        )
      : [];
  }
  if (type === "date") {
    if (!_get(searchLocation, fieldName, "")) return null;
    return dayjsTZ(_get(searchLocation, fieldName, "") as string);
  }

  if (type === "date-range") {
    return [
      _get(searchLocation, `from${capitalize(fieldName)}`, "")
        ? dayjsTZ(
            _get(searchLocation, `from${capitalize(fieldName)}`, "") as string
          )
        : null,
      _get(searchLocation, `to${capitalize(fieldName)}`, "")
        ? dayjsTZ(
            _get(searchLocation, `to${capitalize(fieldName)}`, "") as string
          )
        : null,
    ];
  }
  return "";
};

export const checkMainLan = () => {
  const lan = loadLocalStorage("lan");
  if (lan === LanguageID.vi) return true;
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getLanContent = (row: any, field: string) => {
  const fieldName = checkMainLan() ? `native${capitalize(field)}` : field;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return row?.[fieldName || ""] || "";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fixNumber = (number: any): number => {
  if (typeof number === "number") {
    return number.toFixed(2) as unknown as number;
  }
  return number as number;
};

export const formatNumber = (value: string | number) => {
  return new Intl.NumberFormat("en-US").format(Number(value || 0));
};

export const getMapObjectFromArray = (
  options: { label: string; value: string | number }[]
): Record<string | number, string> => {
  return options.reduce((map, obj) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    map[obj.value] = obj.label;
    return map;
  }, {});
};

export const getObjectAutoComplete = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: any,
  configs?: {
    keyLabel?: string;
    keyValue?: string;
    type?: "string" | "number";
    multiLan?: boolean;
  }
) => {
  const {
    keyLabel = "name",
    keyValue = "id",
    type = "string",
    multiLan = false,
  } = configs || {};
  if (!obj) return undefined as never;
  return {
    label: multiLan ? getLanContent(obj, keyLabel) : obj?.[keyLabel] || "",
    value:
      type === "number"
        ? Number(obj?.[keyValue] || 0)
        : String(obj?.[keyValue] || ""),
    original: obj,
  } as never;
};

export function maskEmail(email?: string): string {
  if (!email) return "";
  const [username = "", domain = ""] = email.split("@");
  const maskedDomain = "*".repeat(domain.length);
  return `${username}${maskedDomain ? `@${maskedDomain}` : ""}`;
}

export function maskPhoneNumber(phoneNumber?: string | number): string {
  if (!phoneNumber) return "";
  const visiblePart = String(phoneNumber).slice(-3);
  const maskedPart = "*".repeat(String(phoneNumber).length - 3);
  return `${maskedPart}${visiblePart}`;
}

export function roundNumber(payload: number): number {
  return Math.round((payload + Number.EPSILON) * 100) / 100;
}
export const getListRowError = (
  data: { rowId: number; message: string }[],
  currentData: { rowId: number; message: string }[]
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line array-callback-return
  const result = currentData.reduce((acc, cur) => {
    const errorItem = data?.find(
      (item) => Number(item.rowId) === Number(cur?.rowId) && item?.message
    );
    if (errorItem) {
      acc.push({
        ...cur,
        message: errorItem?.message,
      } as never);
    }
    return acc;
  }, []);

  return result;
};

export const getListRowErrorEmployee = (
  data: { rowId: number; message: string }[],
  currentData: { rowId: number; message: string }[]
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line array-callback-return
  const result = currentData.reduce((acc, cur) => {
    const errorItem = data?.find(
      (item) =>
        Number(item.rowId) === Number(+(cur?.rowId || 0) - 1) && item?.message
    );
    if (errorItem) {
      acc.push({
        ...cur,
        message: errorItem?.message,
      } as never);
    }
    return acc;
  }, []);

  return result;
};

export const checkImportSuccess = (data: { message?: string }[]) => {
  return !Array.isArray(data) ? true : data?.every((item) => !item?.message);
};

export const getLabelPayrollCycle = (item: PayrollCycle) => {
  if (item.type === PayrollCycleType.Period) {
    return `Kỳ ${item?.period || ""} (${item?.from} - ${item?.to})`;
  }
  if (item.type === PayrollCycleType.Month) {
    return `Tròn tháng (${item?.from} - ${item?.to})`;
  }
  return "";
};

export const getValueEmptyInExcel = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any,
  defaultValue?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  if (String(input) === "null" || String(input) === "undefined") {
    return defaultValue || "";
  }
  return input;
};

export function padToTwoDigits(num: number): string {
  return num.toString().padStart(2, "0");
}

export function getDateByIndex({
  fromDate,
  toDate,
  index,
}: {
  fromDate: Dayjs | string;
  toDate: Dayjs | string;
  index: number;
}): Dayjs | null {
  const start = dayjsFormatTZ(fromDate, "DD-MM-YYYY");
  const end = dayjsFormatTZ(toDate, "DD-MM-YYYY");
  const totalDays = end.diff(start, "day");
  if (index < 0 || index > totalDays) return null;
  return start.add(index, "day");
}

export const checkFalsyValue = (input: string) => {
  if (input === "null" || input === "undefined") return true;
  return false;
};

export function extractFilename(
  input?: string | null,
  hasExtension = true
): string {
  if (!input) return "";
  const knownExtensions = [".xlsx", ".xls"];
  let name = input;
  let extension = "";

  const matchedExtension = knownExtensions.find((ext) =>
    input.toLowerCase().endsWith(ext)
  );

  if (matchedExtension) {
    extension = input.slice(input.length - matchedExtension.length);
    name = input.slice(0, input.length - matchedExtension.length);
  }
  const finalName = name.replace(/-?\b\d{13}\b/g, "");
  return `${finalName}${hasExtension ? extension : ""}`;
}
